import type {
  DirectiveBinding, ObjectDirective, Ref,
} from 'vue';

import zIndexScale from '../../styles/exports/z-index.export.module.scss';
import {
  rafCallback,
} from '../../utils/raf.ts';

import {
  IQTooltipHoverSubscriber,
  isPointerOverTarget,
  trackUnreachableHover,
  untrackUnreachableHover,
} from './q-tooltip-hover.ts';
import {
  IQTooltipRect,
  TOOLTIP_GUTTER,
  TQTooltipPosition,
  TQTooltipSizes,
  intersectRect,
  rectsOverlap,
  resolveLockedPlacement,
  resolvePlacement,
} from './q-tooltip-position.ts';

/** An element, a template ref, or a selector resolved against the target's ancestors. */
export type TQTooltipContainer = HTMLElement | Ref<HTMLElement | null> | string;

export interface IQTooltipOptions {
  value?: string | null,
  /** Side to place the tooltip on. Equivalent to the `v-tooltip.bottom` modifier, and wins over it. */
  position?: TQTooltipPosition,
  /**
   * Escape `value` instead of parsing it as markup. On by default; only turn it off for
   * strings the application itself composed, never for anything a user can influence.
   */
  escape?: boolean,
  /** Clipping boundary the tooltip tries to stay inside of. Defaults to the viewport. */
  container?: TQTooltipContainer,
  /** `320`, `'24rem'` or `'none'`. Defaults to the theme's cap. */
  maxWidth?: number | string,
}

export type TQTooltipBinding = string | IQTooltipOptions | null | undefined;

const POSITIONS: TQTooltipPosition[] = [
  'top',
  'right',
  'bottom',
  'left',
];

const DEFAULT_POSITION: TQTooltipPosition = 'right';

interface IQTooltipState {
  target: HTMLElement,
  options: IQTooltipOptions,
  preferredPosition: TQTooltipPosition,
  tooltipElement: HTMLElement | null,
  textElement: HTMLElement | null,
  arrowElement: HTMLElement | null,
  /** Side the visible tooltip was placed on. Kept for its whole lifetime so it cannot flip mid-scroll. */
  shownPosition: TQTooltipPosition | null,
  shownValue: string | null,
  tooltipSizes: TQTooltipSizes | null,
  resizeObserver: ResizeObserver | null,
  hoverSubscriber: IQTooltipHoverSubscriber,
  scheduleReposition: () => void,
  onPointerEnter: () => void,
  onPointerLeave: (event: MouseEvent) => void,
  onTargetClick: () => void,
  onKeyDown: (event: KeyboardEvent) => void,
}

/**
 * The component that rendered a binding. Vue merges the directives a component puts on its
 * own root with the ones its parent adds, so one element can carry several tooltips; the
 * owner is what tells them apart.
 */
type TQTooltipOwner = DirectiveBinding<TQTooltipBinding>['instance'];

const tooltipStates = new WeakMap<HTMLElement, Map<TQTooltipOwner, IQTooltipState>>();

const reportedTargets = new WeakSet<HTMLElement>();

function readState(target: HTMLElement, binding: DirectiveBinding<TQTooltipBinding>): IQTooltipState | undefined {
  return tooltipStates.get(target)?.get(binding.instance);
}

function storeState(target: HTMLElement, binding: DirectiveBinding<TQTooltipBinding>, state: IQTooltipState): void {
  const statesByOwner = tooltipStates.get(target) ?? new Map<TQTooltipOwner, IQTooltipState>();
  statesByOwner.set(binding.instance, state);
  tooltipStates.set(target, statesByOwner);
}

function dropState(target: HTMLElement, binding: DirectiveBinding<TQTooltipBinding>): void {
  const statesByOwner = tooltipStates.get(target);
  if (!statesByOwner) {
    return;
  }
  statesByOwner.delete(binding.instance);
  if (statesByOwner.size === 0) {
    tooltipStates.delete(target);
  }
}

/**
 * Two tooltips on one element both show, stacked on top of each other. The usual cause is a
 * component that already tooltips its own root being handed another one by its parent, which
 * should pass a prop the component forwards instead.
 */
function reportCompetingTooltipsOnce(target: HTMLElement): void {
  if (reportedTargets.has(target)) {
    return;
  }
  const values = [
    ...tooltipStates.get(target)?.values() ?? [],
  ]
    .map((state) => state.options.value)
    .filter(Boolean);

  if (values.length < 2) {
    return;
  }
  reportedTargets.add(target);
  console.warn('[q-tooltip] Two tooltips are bound to the same element and will both be shown:', values, target);
}

function toRect(domRect: DOMRect): IQTooltipRect {
  return {
    left: domRect.left,
    top: domRect.top,
    width: domRect.width,
    height: domRect.height,
  };
}

function getViewportRect(): IQTooltipRect {
  return {
    left: 0,
    top: 0,
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
  };
}

function normalizeOptions(binding: DirectiveBinding<TQTooltipBinding>): IQTooltipOptions {
  if (typeof binding.value === 'string') {
    return {
      value: binding.value,
    };
  }
  if (!binding.value) {
    return {};
  }
  return binding.value;
}

function resolvePreferredPosition(options: IQTooltipOptions, binding: DirectiveBinding<TQTooltipBinding>): TQTooltipPosition {
  if (options.position) {
    return options.position;
  }
  return POSITIONS.find((position) => binding.modifiers[position]) ?? DEFAULT_POSITION;
}

function resolveContainerElement(state: IQTooltipState): HTMLElement | null {
  const container = state.options.container;
  if (!container) {
    return null;
  }
  if (typeof container === 'string') {
    return state.target.closest<HTMLElement>(container) ?? document.querySelector<HTMLElement>(container);
  }
  if (container instanceof HTMLElement) {
    return container;
  }
  return container.value;
}

function resolveClippingBounds(state: IQTooltipState): IQTooltipRect {
  const viewportRect = getViewportRect();
  const containerElement = resolveContainerElement(state);
  if (!containerElement) {
    return viewportRect;
  }
  return intersectRect(toRect(containerElement.getBoundingClientRect()), viewportRect);
}

function resolveMaxWidth(maxWidth: IQTooltipOptions['maxWidth']): string {
  if (maxWidth === undefined) {
    return '';
  }
  return typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
}

function renderValue(textElement: HTMLElement, value: string, escape: IQTooltipOptions['escape']): void {
  if (escape === false) {
    textElement.innerHTML = value;
  } else {
    textElement.textContent = value;
  }
}

interface IQTooltipMountedElements {
  tooltipElement: HTMLElement,
  textElement: HTMLElement,
}

function mountTooltip(state: IQTooltipState, value: string): IQTooltipMountedElements {
  const arrowElement = document.createElement('div');
  arrowElement.className = 'p-tooltip-arrow';

  const textElement = document.createElement('div');
  textElement.className = 'p-tooltip-text';

  const tooltipElement = document.createElement('div');
  tooltipElement.className = 'p-tooltip p-component';
  tooltipElement.append(arrowElement, textElement);

  Object.assign(tooltipElement.style, {
    position: 'fixed',
    display: 'block',
    pointerEvents: 'none',
    zIndex: zIndexScale.tooltip,
  });

  renderValue(textElement, value, state.options.escape);
  document.body.appendChild(tooltipElement);

  state.tooltipElement = tooltipElement;
  state.textElement = textElement;
  state.arrowElement = arrowElement;
  state.shownValue = value;

  return {
    tooltipElement,
    textElement,
  };
}

function applySideClass(tooltipElement: HTMLElement, shownPosition: TQTooltipPosition | null): void {
  POSITIONS.forEach((position) => {
    tooltipElement.classList.toggle(`p-tooltip-${position}`, position === shownPosition);
  });
}

function measureOnSide(tooltipElement: HTMLElement, position: TQTooltipPosition): IQTooltipRect {
  applySideClass(tooltipElement, position);
  const sideRect = tooltipElement.getBoundingClientRect();
  return {
    left: 0,
    top: 0,
    width: sideRect.width,
    height: sideRect.height,
  };
}

/**
 * Wrap the text once at a neutral position, pin it, then measure the box on every side.
 *
 * A shrink-to-fit box resolves against `containingBlock.width - left`, so wrapping it after
 * it has been pushed towards an edge collapses the text into a one character per line
 * column. Pinning the text makes the box rigid, which is what lets the placement cascade
 * trust its measurements; the width cap has done its job by then and is lifted. The theme
 * pads the box differently on each side to make room for the arrow, so one measurement
 * cannot stand in for all four.
 */
function measureTooltip(tooltipElement: HTMLElement, textElement: HTMLElement, maxWidth: IQTooltipOptions['maxWidth']): TQTooltipSizes {
  applySideClass(tooltipElement, null);
  tooltipElement.style.left = '0px';
  tooltipElement.style.top = '0px';
  tooltipElement.style.maxWidth = resolveMaxWidth(maxWidth);
  textElement.style.width = '';

  const viewportCap = getViewportRect().width - TOOLTIP_GUTTER * 2;
  if (tooltipElement.getBoundingClientRect().width > viewportCap) {
    tooltipElement.style.maxWidth = `${viewportCap}px`;
  }

  textElement.style.width = `${Math.ceil(textElement.getBoundingClientRect().width)}px`;
  tooltipElement.style.maxWidth = 'none';

  return {
    top: measureOnSide(tooltipElement, 'top'),
    right: measureOnSide(tooltipElement, 'right'),
    bottom: measureOnSide(tooltipElement, 'bottom'),
    left: measureOnSide(tooltipElement, 'left'),
  };
}

function placeTooltip(state: IQTooltipState): void {
  const tooltipElement = state.tooltipElement;
  const arrowElement = state.arrowElement;
  const tooltipSizes = state.tooltipSizes;
  if (!tooltipElement || !arrowElement || !tooltipSizes) {
    return;
  }

  const targetRect = toRect(state.target.getBoundingClientRect());
  const viewportRect = getViewportRect();
  const containerElement = resolveContainerElement(state);

  const placement = state.shownPosition
    ? resolveLockedPlacement(state.shownPosition, targetRect, tooltipSizes[state.shownPosition], viewportRect)
    : resolvePlacement(
      state.preferredPosition,
      targetRect,
      tooltipSizes,
      viewportRect,
      containerElement ? toRect(containerElement.getBoundingClientRect()) : undefined,
    );

  applySideClass(tooltipElement, placement.position);
  tooltipElement.style.left = `${Math.round(placement.left)}px`;
  tooltipElement.style.top = `${Math.round(placement.top)}px`;

  const arrowOffset = `${Math.round(placement.arrowOffset)}px`;
  const isVerticalSide = placement.position === 'top' || placement.position === 'bottom';
  Object.assign(arrowElement.style, {
    top: isVerticalSide
      ? (placement.position === 'bottom' ? '0px' : '')
      : arrowOffset,
    bottom: placement.position === 'top' ? '0px' : '',
    left: isVerticalSide
      ? arrowOffset
      : (placement.position === 'right' ? '0px' : ''),
    right: placement.position === 'left' ? '0px' : '',
  });

  state.shownPosition = placement.position;
}

function repositionTooltip(state: IQTooltipState): void {
  if (!state.tooltipElement) {
    return;
  }
  if (!state.target.isConnected) {
    hideTooltip(state);
    return;
  }
  if (!rectsOverlap(toRect(state.target.getBoundingClientRect()), resolveClippingBounds(state))) {
    hideTooltip(state);
    return;
  }
  placeTooltip(state);
}

function bindRepositionListeners(state: IQTooltipState): void {
  document.addEventListener('scroll', state.scheduleReposition, {
    capture: true,
    passive: true,
  });
  window.addEventListener('resize', state.scheduleReposition);
  document.addEventListener('keydown', state.onKeyDown);
  state.resizeObserver = new ResizeObserver(state.scheduleReposition);
  state.resizeObserver.observe(state.target);
}

function unbindRepositionListeners(state: IQTooltipState): void {
  document.removeEventListener('scroll', state.scheduleReposition, {
    capture: true,
  });
  window.removeEventListener('resize', state.scheduleReposition);
  document.removeEventListener('keydown', state.onKeyDown);
  state.resizeObserver?.disconnect();
  state.resizeObserver = null;
}

function showTooltip(state: IQTooltipState): void {
  const value = state.options.value;
  if (!value || state.tooltipElement || !state.target.isConnected) {
    return;
  }

  const {
    tooltipElement, textElement,
  } = mountTooltip(state, value);
  state.tooltipSizes = measureTooltip(tooltipElement, textElement, state.options.maxWidth);
  placeTooltip(state);
  bindRepositionListeners(state);
}

function hideTooltip(state: IQTooltipState): void {
  if (!state.tooltipElement) {
    return;
  }
  unbindRepositionListeners(state);
  state.tooltipElement.remove();
  state.tooltipElement = null;
  state.textElement = null;
  state.arrowElement = null;
  state.shownPosition = null;
  state.shownValue = null;
  state.tooltipSizes = null;
}

function bindTargetListeners(state: IQTooltipState, shouldBind: boolean): void {
  if (shouldBind) {
    state.target.addEventListener('mouseenter', state.onPointerEnter);
    state.target.addEventListener('mouseleave', state.onPointerLeave);
    state.target.addEventListener('click', state.onTargetClick);
    trackUnreachableHover(state.hoverSubscriber);
    return;
  }
  state.target.removeEventListener('mouseenter', state.onPointerEnter);
  state.target.removeEventListener('mouseleave', state.onPointerLeave);
  state.target.removeEventListener('click', state.onTargetClick);
  untrackUnreachableHover(state.hoverSubscriber);
}

function createState(target: HTMLElement, binding: DirectiveBinding<TQTooltipBinding>): IQTooltipState {
  const options = normalizeOptions(binding);
  const state: IQTooltipState = {
    target,
    options,
    preferredPosition: resolvePreferredPosition(options, binding),
    tooltipElement: null,
    textElement: null,
    arrowElement: null,
    shownPosition: null,
    shownValue: null,
    tooltipSizes: null,
    resizeObserver: null,
    hoverSubscriber: {
      target,
      onHoverStart: () => showTooltip(state),
      onHoverEnd: () => hideTooltip(state),
    },
    scheduleReposition: () => {},
    onPointerEnter: () => {},
    onPointerLeave: () => {},
    onTargetClick: () => {},
    onKeyDown: () => {},
  };

  state.scheduleReposition = rafCallback(() => repositionTooltip(state));
  state.onPointerEnter = () => showTooltip(state);
  state.onPointerLeave = (event: MouseEvent) => {
    // The target turned unreachable under a resting pointer; the document-level tracker
    // takes over instead of letting the tooltip blink out and back in.
    const enteredElement = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    const pointerPosition = {
      x: event.clientX,
      y: event.clientY,
    };
    if (isPointerOverTarget(target, enteredElement, pointerPosition)) {
      return;
    }
    hideTooltip(state);
  };
  state.onTargetClick = () => hideTooltip(state);
  state.onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      hideTooltip(state);
    }
  };

  return state;
}

const QTooltip: ObjectDirective<HTMLElement, TQTooltipBinding> = {
  mounted(target, binding) {
    const state = createState(target, binding);
    storeState(target, binding, state);
    bindTargetListeners(state, Boolean(state.options.value));
    reportCompetingTooltipsOnce(target);
  },

  updated(target, binding) {
    const state = readState(target, binding);
    if (!state) {
      return;
    }

    state.options = normalizeOptions(binding);
    state.preferredPosition = resolvePreferredPosition(state.options, binding);

    const value = state.options.value;
    bindTargetListeners(state, Boolean(value));
    reportCompetingTooltipsOnce(target);

    if (!value) {
      hideTooltip(state);
      return;
    }
    if (state.tooltipElement && state.textElement && value !== state.shownValue) {
      renderValue(state.textElement, value, state.options.escape);
      state.shownValue = value;
      state.tooltipSizes = measureTooltip(state.tooltipElement, state.textElement, state.options.maxWidth);
      state.shownPosition = null;
      placeTooltip(state);
    }
  },

  unmounted(target, binding) {
    const state = readState(target, binding);
    if (!state) {
      return;
    }
    hideTooltip(state);
    bindTargetListeners(state, false);
    dropState(target, binding);
  },
};

export default QTooltip;
